import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { SectionHero, AdminCard, StatusPill } from '@/components/admin/primitives'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Search, Video, Database, Code, RefreshCw, Wrench, ExternalLink, Download, Play, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { searchVideos, getTelemetry, getAnalysisRuns, rerunAnalysis, cancelAnalysis, repairVideos, type DebugVideo, type DebugTelemetryResponse, type DebugAnalysisRun } from '@/lib/api/admin/debug'
import { formatDateTime } from '@/lib/time/formatDate'

export default function DebugTools() {
  const [activeTab, setActiveTab] = useState<'video' | 'telemetry' | 'analysis'>('video')
  
  // Video search state
  const [videoSearchId, setVideoSearchId] = useState('')
  const [videoSearchType, setVideoSearchType] = useState<'video_id' | 'playback_id' | 'asset_id'>('playback_id')
  const [videoSearchTrigger, setVideoSearchTrigger] = useState(0)

  // Telemetry state
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)

  // Analysis state
  const [analysisVideoId, setAnalysisVideoId] = useState('')
  const [analysisSearchTrigger, setAnalysisSearchTrigger] = useState(0)

  const { data: videoResults, isLoading: videoLoading, refetch: refetchVideos } = useQuery({
    queryKey: ['admin-debug-videos', videoSearchType, videoSearchId, videoSearchTrigger],
    queryFn: () => {
      if (!videoSearchId.trim()) throw new Error('Search ID required')
      if (videoSearchType === 'video_id') {
        return searchVideos(videoSearchId.trim())
      } else if (videoSearchType === 'asset_id') {
        return searchVideos(undefined, videoSearchId.trim())
      } else {
        return searchVideos(undefined, undefined, videoSearchId.trim())
      }
    },
    enabled: videoSearchTrigger > 0 && !!videoSearchId.trim(),
    retry: false,
  })

  const { data: telemetryData, isLoading: telemetryLoading } = useQuery<DebugTelemetryResponse>({
    queryKey: ['admin-debug-telemetry', selectedRunId],
    queryFn: () => {
      if (!selectedRunId) throw new Error('Run ID required')
      return getTelemetry(selectedRunId)
    },
    enabled: !!selectedRunId,
    retry: false,
  })

  const { data: analysisRunsData, isLoading: analysisLoading, refetch: refetchAnalysis } = useQuery({
    queryKey: ['admin-debug-analysis', analysisVideoId, analysisSearchTrigger],
    queryFn: () => {
      if (!analysisVideoId.trim()) throw new Error('Video asset ID required')
      return getAnalysisRuns(analysisVideoId.trim())
    },
    enabled: analysisSearchTrigger > 0 && !!analysisVideoId.trim(),
    retry: false,
  })

  const rerunMutation = useMutation({
    mutationFn: rerunAnalysis,
    onSuccess: () => {
      toast.success('Analysis rerun created')
      refetchAnalysis()
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to rerun analysis')
    },
  })

  const cancelMutation = useMutation({
    mutationFn: cancelAnalysis,
    onSuccess: () => {
      toast.success('Analysis cancelled')
      refetchAnalysis()
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to cancel analysis')
    },
  })

  const repairMutation = useMutation({
    mutationFn: ({ action, videoId }: { action: 'fix_playback_id' | 'sync_mux'; videoId?: string }) =>
      repairVideos(action, videoId),
    onSuccess: (data) => {
      toast.success(data.message || `Repaired ${data.repaired} videos`)
      refetchVideos()
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Repair failed')
    },
  })

  const handleVideoSearch = () => {
    if (!videoSearchId.trim()) {
      toast.error('Please enter a search ID')
      return
    }
    setVideoSearchTrigger(prev => prev + 1)
  }

  const handleAnalysisSearch = () => {
    if (!analysisVideoId.trim()) {
      toast.error('Please enter a video asset ID')
      return
    }
    setAnalysisSearchTrigger(prev => prev + 1)
  }

  const handleDownloadTelemetry = () => {
    if (!telemetryData?.run) {
      toast.error('No telemetry data to download')
      return
    }

    const jsonContent = JSON.stringify(telemetryData.run, null, 2)
    const blob = new Blob([jsonContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `telemetry-${selectedRunId}-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    toast.success('Telemetry JSON downloaded')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
      case 'completed':
        return 'healthy'
      case 'processing':
      case 'pending':
        return 'pending'
      case 'uploading':
        return 'pending'
      case 'errored':
      case 'failed':
        return 'critical'
      default:
        return 'pending'
    }
  }

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="Debug Tools"
          description="Video, telemetry, and analysis debugging utilities"
          gradient="warning"
        />

        {/* Tabs */}
        <div className="mt-8">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-4">
            <TabsList>
              <TabsTrigger value="video">
                <Video className="w-4 h-4 mr-2" />
                Video
              </TabsTrigger>
              <TabsTrigger value="telemetry">
                <Database className="w-4 h-4 mr-2" />
                Telemetry
              </TabsTrigger>
              <TabsTrigger value="analysis">
                <Code className="w-4 h-4 mr-2" />
                Analysis
              </TabsTrigger>
            </TabsList>

            {/* Video Tab */}
            <TabsContent value="video">
              <AdminCard title="Video Search" accentColor="info">
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Select value={videoSearchType} onValueChange={(v) => setVideoSearchType(v as any)}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="video_id">Video ID</SelectItem>
                        <SelectItem value="playback_id">Playback ID</SelectItem>
                        <SelectItem value="asset_id">Asset ID</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      value={videoSearchId}
                      onChange={(e) => setVideoSearchId(e.target.value)}
                      placeholder={`Enter ${videoSearchType}...`}
                      onKeyDown={(e) => e.key === 'Enter' && handleVideoSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleVideoSearch} disabled={videoLoading}>
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </div>

                  {videoLoading && (
                    <p className="text-muted-foreground">Searching...</p>
                  )}

                  {videoResults && videoResults.videos.length > 0 && (
                    <div className="space-y-3">
                      {videoResults.videos.map((video) => (
                        <div key={video.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium">Video {video.id.slice(0, 8)}</span>
                                <StatusPill status={getStatusColor(video.status)} label={video.status} />
                              </div>
                              <div className="text-sm text-muted-foreground space-y-1">
                                <p>Asset ID: {video.mux_asset_id || 'N/A'}</p>
                                <p>Playback ID: {video.mux_playback_id || 'N/A'}</p>
                                <p>Created: {formatDateTime(video.created_at)}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" asChild>
                                <Link to={`/analysis/${video.id}`}>
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  View Analysis
                                </Link>
                              </Button>
                              {!video.mux_playback_id && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => repairMutation.mutate({ action: 'fix_playback_id', videoId: video.id })}
                                  disabled={repairMutation.isPending}
                                >
                                  <Wrench className="w-4 h-4 mr-2" />
                                  Repair
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {videoResults.videos.some(v => !v.mux_playback_id) && (
                        <Button
                          onClick={() => repairMutation.mutate({ action: 'fix_playback_id' })}
                          disabled={repairMutation.isPending}
                          variant="outline"
                          className="w-full"
                        >
                          <Wrench className="w-4 h-4 mr-2" />
                          {repairMutation.isPending ? 'Repairing...' : 'Repair All Missing Playback IDs'}
                        </Button>
                      )}
                    </div>
                  )}

                  {videoResults && videoResults.videos.length === 0 && videoSearchTrigger > 0 && (
                    <p className="text-muted-foreground">No videos found</p>
                  )}
                </div>
              </AdminCard>
            </TabsContent>

            {/* Telemetry Tab */}
            <TabsContent value="telemetry">
              <AdminCard title="Telemetry Data Inspection" accentColor="info">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="run-id">Run ID</Label>
                    <div className="flex gap-2">
                      <Input
                        id="run-id"
                        value={selectedRunId || ''}
                        onChange={(e) => setSelectedRunId(e.target.value.trim() || null)}
                        placeholder="Enter run ID (UUID)..."
                        onKeyDown={(e) => e.key === 'Enter' && selectedRunId && setSelectedRunId(selectedRunId)}
                        className="flex-1"
                      />
                      <Button
                        onClick={() => setSelectedRunId(null)}
                        variant="outline"
                        size="icon"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {telemetryLoading && (
                    <p className="text-muted-foreground">Loading telemetry...</p>
                  )}

                  {telemetryData && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          <StatusPill
                            status={telemetryData.has_telemetry_ai ? 'healthy' : 'critical'}
                            label={`Telemetry AI: ${telemetryData.has_telemetry_ai ? 'Yes' : 'No'}`}
                          />
                          <StatusPill
                            status={telemetryData.has_ai_meta ? 'healthy' : 'critical'}
                            label={`AI Meta: ${telemetryData.has_ai_meta ? 'Yes' : 'No'}`}
                          />
                          <StatusPill
                            status={telemetryData.has_ai_mediapipe_v1 ? 'healthy' : 'critical'}
                            label={`MediaPipe: ${telemetryData.has_ai_mediapipe_v1 ? 'Yes' : 'No'}`}
                          />
                        </div>
                        <Button onClick={handleDownloadTelemetry} variant="outline" size="sm">
                          <Download className="w-4 h-4 mr-2" />
                          Download JSON
                        </Button>
                      </div>
                      <div className="p-3 bg-background rounded border text-xs font-mono overflow-auto max-h-96">
                        <pre>{JSON.stringify(telemetryData.run, null, 2)}</pre>
                      </div>
                    </div>
                  )}

                  {!selectedRunId && (
                    <p className="text-muted-foreground">Enter a run ID to inspect telemetry data</p>
                  )}
                </div>
              </AdminCard>
            </TabsContent>

            {/* Analysis Tab */}
            <TabsContent value="analysis">
              <AdminCard title="Analysis Runs" accentColor="info">
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      value={analysisVideoId}
                      onChange={(e) => setAnalysisVideoId(e.target.value)}
                      placeholder="Enter video asset ID..."
                      onKeyDown={(e) => e.key === 'Enter' && handleAnalysisSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleAnalysisSearch} disabled={analysisLoading}>
                      <Search className="w-4 h-4 mr-2" />
                      Load Runs
                    </Button>
                  </div>

                  {analysisLoading && (
                    <p className="text-muted-foreground">Loading analysis runs...</p>
                  )}

                  {analysisRunsData && analysisRunsData.runs.length > 0 && (
                    <div className="space-y-3">
                      {analysisRunsData.runs.map((run) => (
                        <div key={run.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium">Run {run.id.slice(0, 8)}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatDateTime(run.created_at)}
                              </p>
                              <div className="flex gap-2 mt-2">
                                <StatusPill
                                  status={getStatusColor(run.status)}
                                  label={run.status}
                                />
                                <StatusPill
                                  status={run.telemetry_ai ? 'healthy' : 'critical'}
                                  label={`Telemetry: ${run.telemetry_ai ? 'Yes' : 'No'}`}
                                />
                                <StatusPill
                                  status={run.ai_meta ? 'healthy' : 'critical'}
                                  label={`AI Meta: ${run.ai_meta ? 'Yes' : 'No'}`}
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              {run.status === 'pending' || run.status === 'processing' ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => cancelMutation.mutate(run.id)}
                                  disabled={cancelMutation.isPending}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Cancel
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => rerunMutation.mutate(run.id)}
                                  disabled={rerunMutation.isPending}
                                >
                                  <Play className="w-4 h-4 mr-2" />
                                  Re-run
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {analysisRunsData && analysisRunsData.runs.length === 0 && analysisSearchTrigger > 0 && (
                    <p className="text-muted-foreground">No analysis runs found for this video</p>
                  )}
                </div>
              </AdminCard>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}

